import {
    MailOutlined as MailOutlinedIcon,
    PlaylistAdd as PlaylistAddIcon,
    PlaylistAddCheckRounded as PlaylistAddCheckRoundedIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import is from '@sindresorhus/is';
import { Fragment, useContext } from 'react';
import { matchPath } from 'react-router';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '../../../../../../../shared/source/context/context';
import { SecondaryActionButton } from '../../../../../design/components/button/SecondaryAction';
import { InlineLoadingSpinner } from '../../../../../design/components/LoadingSpinner';
import { SkillsMatchingCard } from '../../../../common/SkillsMatchingCard';
import { getUserName } from '../../../../common/user';
import { ComponentErrorBoundary } from '../../../../error-handling/ErrorBoundaries';
import { useSnackbars } from '../../../../error-handling/SnackBars';
import {
    useJobPermissions,
    useJobShortListMutations,
    useRelatedToJobCandidateQuery,
    useShortListPermissions,
} from '../../../../query-hooks/jobs';
import { useOneUserQuery } from '../../../../query-hooks/users';
import { useCandidateMatchingInfoQuery } from '../../../../query-hooks/users/jobs';
import { route as jobListRoute } from '../../../job-list/constants';
import { Ambitions } from '../../common-section/ambitions/Ambitions';
import { GeneralInformation } from '../../common-section/GeneralInformation';
import { Languages } from '../../common-section/languages/Language';
import { Sidebar } from '../../common-section/sidebar/SideBar';
import { Skills } from '../../common-section/Skills';
import { route } from '../route';

import type { JobCandidate } from '../../../../../../../shared/source/data-contract/job';
import type { FunctionComponent, ReactElement } from 'react';

type UserContactButtonProps = {
    readonly userEmail?: string | undefined;
};

const UserContactButton: FunctionComponent<UserContactButtonProps> = ({ userEmail }) => {
    const { translate } = useContext(ApplicationContext);

    if (is.undefined(userEmail)) {
        return null;
    }

    return (
        <SecondaryActionButton
            label={translate('pages.recruiter-profile.general-information.contact')}
            startIcon={<MailOutlinedIcon />}
            onClick={() => window.open(`mailto:${userEmail}`)}
        />
    );
};

type CandidateMatchingSectionProps = {
    readonly candidate: JobCandidate;
    readonly jobId: string;
};

// eslint-disable-next-line complexity
const CandidateMatchingSection: FunctionComponent<CandidateMatchingSectionProps> = ({ candidate, jobId }) => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { myUserProfile, userIsAnonymized } = applicationState;
    const isCurrentUsersProfile = myUserProfile.get('id').mapOr(false, (myUserProfileId) => {
        return myUserProfileId === candidate.id;
    });
    const isAnonymizationActive = userIsAnonymized && !isCurrentUsersProfile;

    const { canUserGetCandidate } = useJobPermissions(jobId);

    const { data, isPending } = useCandidateMatchingInfoQuery({
        enabled: Boolean(canUserGetCandidate),
        jobId,
        userId: candidate.id,
    });

    if (is.undefined(data?.matchingSkills) || is.undefined(data?.missingSkills)) {
        return null;
    }

    const candidateFirstName = isAnonymizationActive
        ? getUserName(Maybe.of(candidate), isAnonymizationActive)
        : candidate.firstName;

    const headline = translate('pages.job-detail.candidate-matching.headline', {
        name: candidateFirstName,
    });
    const description = translate('pages.job-detail.candidate-matching.description', { name: candidateFirstName });
    const mismatchingTitle = translate('pages.job-detail.candidate-matching.mismatches', { name: candidateFirstName });
    const matchingTitle = translate('pages.job-detail.candidate-matching.matches', { name: candidateFirstName });

    return (
        <Fragment>
            {isPending ? (
                <InlineLoadingSpinner />
            ) : (
                <SkillsMatchingCard
                    description={description}
                    headline={headline}
                    matchingTitle={matchingTitle}
                    mismatchingTitle={mismatchingTitle}
                    recommendationData={data}
                    showRelevantLearningOpportunities={false}
                />
            )}
        </Fragment>
    );
};

export const CandidatePreview: FunctionComponent = () => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const { location, locationSearchParams } = applicationState;
    const candidateId = locationSearchParams.get(route.searchParams.profilePreviewUserId) ?? '';
    const jobId =
        matchPath(jobListRoute.patterns.job.details, location.pathname)?.params[jobListRoute.params.jobId] ?? '';
    const { addToShortListMutation, removeFromShortListMutation } = useJobShortListMutations(jobId);
    const { canUserAddToShortList, canUserRemoveFromShortList } = useShortListPermissions(jobId);
    const { userQuery } = useOneUserQuery(candidateId);
    const { data: candidate, isPending } = useRelatedToJobCandidateQuery({ jobId, userId: candidateId });
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();

    if (is.undefined(candidate) || isPending) {
        return null;
    }

    const getShortListButton = (): ReactElement<any> | null => {
        if (canUserRemoveFromShortList && candidate.isOnShortList === true) {
            const removeFromShortList = (): void =>
                removeFromShortListMutation.mutate([candidateId], {
                    onError: showErrorSnackbar,
                    onSuccess: () => showSuccessSnackbar('success.shortlist.removed'),
                });

            return (
                <Fragment>
                    <SecondaryActionButton
                        isDisabled={removeFromShortListMutation.isPending}
                        label={translate('pages.job-detail.recommendations.shortlisted')}
                        startIcon={<PlaylistAddCheckRoundedIcon />}
                        onClick={removeFromShortList}
                    />
                    <UserContactButton userEmail={userQuery.data?.emailAddress} />
                </Fragment>
            );
        }

        if (canUserAddToShortList) {
            const addToShortList = (): void =>
                addToShortListMutation.mutate([candidateId], {
                    onError: showErrorSnackbar,
                    onSuccess: () => showSuccessSnackbar('success.shortlist.added'),
                });

            return (
                <Fragment>
                    <SecondaryActionButton
                        isDisabled={addToShortListMutation.isPending}
                        label={translate('pages.job-detail.recommendations.shortlist')}
                        startIcon={<PlaylistAddIcon />}
                        onClick={addToShortList}
                    />
                    <UserContactButton userEmail={userQuery.data?.emailAddress} />
                </Fragment>
            );
        }

        return null;
    };

    return (
        <Grid container={true} direction="row" display="grid" gap={2} gridTemplateColumns="2fr 1fr">
            <Grid container={true} gap={2} sx={{ pb: 2 }}>
                <GeneralInformation actionButtons={getShortListButton()} userId={candidateId} />
                <ComponentErrorBoundary message="errors.component.drawer.candidate-matching-info">
                    <CandidateMatchingSection candidate={candidate} jobId={jobId} />
                </ComponentErrorBoundary>
                <ComponentErrorBoundary message="errors.component.drawer.skills">
                    <Skills userId={candidateId} />
                </ComponentErrorBoundary>
                <ComponentErrorBoundary message="errors.component.drawer.languages">
                    <Languages userId={candidateId} />
                </ComponentErrorBoundary>
                <ComponentErrorBoundary message="errors.component.drawer.ambitions">
                    <Ambitions userId={candidateId} />
                </ComponentErrorBoundary>
            </Grid>
            <Grid>
                <Sidebar userId={candidateId} />
            </Grid>
        </Grid>
    );
};
